import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faArrowUp,
  faArrowDown,
  faMinus,
} from "@fortawesome/free-solid-svg-icons";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { detectCsvDataType } from "../../../utils/csvDataType";
import RawFallback from "../../Shared/RawFallback/RawFallback";
import styles from "./CSVTrendsAnalysis.module.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const isNumericCol = (vals) => vals.every((v) => v === "" || !isNaN(parseFloat(String(v).replace(/[^0-9.-]/g, ""))));

const MAX_LEN = 90;
const truncate = (s) => (s.length > MAX_LEN ? s.slice(0, MAX_LEN).trim() + "…" : s);

const dedupe = (items) => {
  const seen = new Set();
  return items.filter((item) => {
    const norm = item.toLowerCase().slice(0, 45).replace(/\s+/g, " ");
    if (seen.has(norm)) return false;
    seen.add(norm);
    return true;
  });
};

const getBullets = (content) => {
  if (!content || !Array.isArray(content)) return [];
  return content
    .map((c) => (c.type === "bullet" ? c.text : c.type === "keyValue" ? `${c.key}: ${c.value}` : c.text))
    .filter(Boolean)
    .map((t) => String(t).trim())
    .filter((t) => t.length > 5);
};

const getTrendDirection = (text) => {
  const lower = text.toLowerCase();
  if (lower.includes("increas") || lower.includes("upward") || lower.includes("growth") || lower.includes("rising"))
    return { icon: faArrowUp, color: "#10b981" };
  if (lower.includes("decreas") || lower.includes("downward") || lower.includes("decline"))
    return { icon: faArrowDown, color: "#ef4444" };
  return { icon: faMinus, color: "#6b7280" };
};

const CSVTrendsAnalysis = ({ data, rawText, analysisData }) => {
  if (!data) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <p>No trends analysis data available</p>
        </div>
      </div>
    );
  }

  const dataType = detectCsvDataType(analysisData?.columns);
  const isML = dataType === "ml_scientific";

  const sectionOrder = isML
    ? ["Temporal Trends", "Value Trends", "Key Findings"]
    : ["Temporal Trends", "Value Trends", "Comparative Trends", "Trend Analysis", "Trends Summary", "Business Trends"];

  const sections = [];
  if (data.sections) {
    const seen = new Set();
    for (const name of sectionOrder) {
      const sec = data.sections.find((s) => {
        const n = (s.name || "").toLowerCase();
        const target = name.toLowerCase();
        return n.includes(target) || target.includes(n);
      });
      if (sec && sec.content?.length && !seen.has(sec.name)) {
        seen.add(sec.name);
        const bullets = dedupe(getBullets(sec.content)).slice(0, 8);
        if (bullets.length > 0) sections.push({ name: sec.name, bullets });
      }
    }
    data.sections.forEach((sec) => {
      if (seen.has(sec.name) || !sec.content?.length) return;
      const bullets = dedupe(getBullets(sec.content)).slice(0, 8);
      if (bullets.length > 0) sections.push({ name: sec.name, bullets });
    });
  }

  let fallbackBullets = [];
  if (sections.length === 0 && rawText) {
    const parts = rawText.split(/SECTION:\s*/i).filter(Boolean);
    parts.forEach((p) => {
      const bullets = (p.match(/- ([^\n]+)/g) || []).map((b) => b.replace(/^- /, "").trim()).filter((t) => t.length > 10);
      fallbackBullets.push(...bullets);
    });
    fallbackBullets = dedupe(fallbackBullets).slice(0, 6);
  }

  const getChartSeriesFromData = () => {
    const rows = analysisData?.data || [];
    const cols = analysisData?.columns || [];
    if (rows.length === 0 || cols.length === 0) return { series: [], xLabels: [] };
    const xCol = cols.find((c) => /^(epoch|step|iteration|time|index|row)$/i.test(String(c))) || cols[0];
    const xIdx = cols.indexOf(xCol);
    const xVals = rows.map((r) => {
      const v = Array.isArray(r) ? r[xIdx] : r[xCol];
      const n = parseFloat(String(v ?? "").replace(/[^0-9.-]/g, ""));
      return !isNaN(n) ? n : rows.indexOf(r);
    });
    const accuracyKeywords = /map|mAP|precision|recall|accuracy|f1|iou|metrics\//i;
    const lossKeywords = /loss|box_loss|seg_loss|cls_loss|train\//i;
    const accuracy = [];
    const losses = [];
    const other = [];
    cols.forEach((col, i) => {
      if (col === xCol) return;
      const vals = rows.map((r) => (Array.isArray(r) ? r[i] : r[col])).map((v) => String(v ?? ""));
      if (!isNumericCol(vals)) return;
      const nums = vals.map((v) => parseFloat(v.replace(/[^0-9.-]/g, "")) || 0);
      if (nums.length <= 1) return;
      const item = { name: col, data: nums };
      if (accuracyKeywords.test(col)) accuracy.push(item);
      else if (lossKeywords.test(col)) losses.push(item);
      else other.push(item);
    });
    const mAP50First = (a, b) => {
      if (/mAP50|map50/i.test(a.name) && !/mAP50|map50/i.test(b.name)) return -1;
      if (!/mAP50|map50/i.test(a.name) && /mAP50|map50/i.test(b.name)) return 1;
      return 0;
    };
    accuracy.sort(mAP50First);
    const series = [...accuracy.slice(0, 3), ...losses.slice(0, 3), ...other.slice(0, 2)].slice(0, 6);
    return { series, xLabels: xVals };
  };

  const { series: chartSeries, xLabels } = getChartSeriesFromData();

  const hasAnyData = sections.length > 0 || fallbackBullets.length > 0 || chartSeries.length > 0;

  if (!hasAnyData) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FontAwesomeIcon icon={faChartLine} />
          </div>
          <div>
            <h2 className={styles.title}>Trends Analysis</h2>
            <p className={styles.subtitle}>Time-based trends and changes</p>
          </div>
        </div>
        <div className={styles.emptyState}>
          <p>No trends data available</p>
          {rawText && <RawFallback rawText={rawText} accentColor="#22c55e" />}
        </div>
      </div>
    );
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top", labels: { color: "#e5e7eb", font: { size: 11 } } },
      tooltip: {
        backgroundColor: "rgba(17, 24, 39, 0.95)",
        titleColor: "#10b981",
        bodyColor: "#e5e7eb",
        padding: 10,
      },
    },
    scales: {
      y: { beginAtZero: false, grid: { color: "rgba(229, 231, 235, 0.1)" }, ticks: { color: "#9ca3af", font: { size: 10 } } },
      x: {
        type: "linear",
        grid: { display: false },
        ticks: {
          color: "#9ca3af",
          font: { size: 10 },
          maxTicksLimit: 20,
        },
      },
    },
  };

  const createLineChartData = (title, dataPoints, xLabelsUsed) => {
    const values = dataPoints.map((val) => {
      if (typeof val === "number") return val;
      const numMatch = val.toString().match(/[\d.]+/);
      return numMatch ? parseFloat(numMatch[0]) : 0;
    });
    const pointRadius = dataPoints.length > 80 ? 0 : dataPoints.length > 40 ? 1 : dataPoints.length > 15 ? 2 : 3;
    const data = xLabelsUsed.map((x, i) => ({ x, y: values[i] }));
    return {
      datasets: [
        {
          label: title,
          data,
          borderColor: "#10b981",
          backgroundColor: "rgba(16, 185, 129, 0.1)",
          borderWidth: 2,
          fill: true,
          tension: 0.4,
          pointRadius,
          spanGaps: false,
        },
      ],
    };
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <FontAwesomeIcon icon={faChartLine} />
        </div>
        <div>
          <h2 className={styles.title}>Trends Analysis</h2>
          <p className={styles.subtitle}>Time-based trends and changes</p>
        </div>
      </div>

      {chartSeries.length > 0 && (
        <div className={styles.chartsSection}>
          <h3 className={styles.sectionTitle}>Trend Visualizations</h3>
          <div className={styles.chartsGrid}>
            {chartSeries.map((s, idx) => (
              <div key={idx} className={styles.chartCard}>
                <div className={styles.chartHeader}>
                  <h3 className={styles.chartTitle}>{s.name}</h3>
                  <div className={styles.trendBadge} style={{ backgroundColor: getTrendDirection(s.name).color + "20", color: getTrendDirection(s.name).color }}>
                    <FontAwesomeIcon icon={getTrendDirection(s.name).icon} />
                  </div>
                </div>
                <div className={styles.lineChartContainer}>
                  <Line data={createLineChartData(s.name, s.data, xLabels.length ? xLabels : s.data.map((_, i) => i + 1))} options={chartOptions} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className={styles.trendsGrid}>
        {sections.map((sec, idx) => (
          <TrendCard key={idx} title={sec.name} bullets={sec.bullets} />
        ))}
        {fallbackBullets.length > 0 && sections.length === 0 && (
          <TrendCard title="Trends" bullets={fallbackBullets} />
        )}
      </div>
    </div>
  );
};

const TrendCard = ({ title, bullets }) => {
  const [expanded, setExpanded] = useState(false);
  const maxShown = 4;
  const hasMore = bullets.length > maxShown;
  const visible = expanded ? bullets : bullets.slice(0, maxShown);

  return (
    <div className={styles.trendCard}>
      <div className={styles.cardHeader}>
        <FontAwesomeIcon icon={faChartLine} className={styles.cardIcon} />
        <h3 className={styles.cardTitle}>{title}</h3>
      </div>
      <ul className={styles.trendList}>
        {visible.map((item, idx) => {
          const trend = getTrendDirection(item);
          return (
            <li key={idx} className={styles.trendItem}>
              <FontAwesomeIcon icon={trend.icon} className={styles.trendArrow} style={{ color: trend.color }} />
              <span className={styles.trendText}>{truncate(item)}</span>
            </li>
          );
        })}
      </ul>
      {hasMore && !expanded && (
        <button type="button" className={styles.showMore} onClick={() => setExpanded(true)}>
          +{bullets.length - maxShown} more
        </button>
      )}
    </div>
  );
};

export default CSVTrendsAnalysis;
